import React from 'react';
import { Box, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import QuestionCard from './QuestionCard';
import SurveyHeader from './Surveyheader';
import { EMOJI_VALUE_LOOKUP, EMOJI_TYPES, DEFAULT_SURVEY_TYPE, isQuestionAnswered } from './surveyHelpers';
import CardSwipeFormLayout from './CardSwipeFormLayout';
/* ============================================================================
   LAYOUT type 0 — "Clásico": todas las preguntas visibles en una sola
   pantalla, una debajo de otra. Es el diseño original / default.
   ========================================================================== */
export const ClassicFormLayout = React.memo(({
  survey,
  sortedQuestions,
  answers,
  unansweredQuestions,
  onRatingChange,
  onSliderChange,
  onSliderCommit,
  onTextChange,
  onRadioChange,
  onEmojiChange,
  t,
}) => (
  <>
    <SurveyHeader title={survey.title} />

    {/* Wrapper con clase propia: permite scopear en CSS la placa numérica
        tipo naipe (ver SurveyResponse.css, sección 5) solo a este layout,
        sin afectar al carrusel swipe (type 2) que reutiliza el mismo
        QuestionCard/.question-number sin ese estilo. */}
    <Box className="classic-layout-questions">
      {sortedQuestions.map(([qId, question], index) => (
        <QuestionCard
          key={qId}
          qId={qId}
          question={question}
          answer={answers[qId]}
          questionNumber={index + 1}
          onRatingChange={onRatingChange}
          onSliderChange={onSliderChange}
          onSliderCommit={onSliderCommit}
          onTextChange={onTextChange}
          onRadioChange={onRadioChange}
          onEmojiChange={onEmojiChange}
          showError={unansweredQuestions.has(qId)}
          t={t}
        />
      ))}
    </Box>
  </>
));
ClassicFormLayout.displayName = 'ClassicFormLayout';

/* ============================================================================
   LAYOUT type 1 — "Stepped": flujo conversacional, una pregunta a la vez,
   con burbuja de resumen de la pregunta anterior y, si la pregunta actual
   ya está contestada, una flechita hacia abajo para continuar.

   La flecha NO se muestra en pasos de tipo texto ni en la última pregunta:
   en esos casos el botón "Siguiente/Enviar" (fijo abajo, ver
   SurveyResponse.jsx + CSS) ya cumple esa función, y mostrar los dos sería
   redundante.

   Tampoco se muestra mientras hay un auto-avance programado para la
   pregunta activa (ver `autoAdvancePending`, calculado en
   SurveyResponse.jsx): si el usuario acaba de contestar, el auto-avance ya
   va a llevarlo a la siguiente pregunta solo, así que la flecha no aporta
   nada y solo generaba un parpadeo (aparecía al contestar y desaparecía al
   avanzar). La flecha únicamente tiene sentido cuando el usuario volvió
   con la burbuja de resumen a una pregunta que YA estaba contestada de
   antes: ahí no hay ningún auto-avance corriendo, y necesita una forma
   manual de seguir.
   ========================================================================== */

// ─── AnswerBadge (respuesta compacta para la burbuja de resumen) ─────────────
const AnswerBadge = React.memo(({ question, answer }) => {
  if (EMOJI_TYPES.has(question.type)) {
    // Lookup precalculado a nivel de módulo en surveyHelpers.js (antes se
    // recorría el set completo con Object.entries().find() + regex en
    // cada render de la burbuja de resumen).
    const src = EMOJI_VALUE_LOOKUP[question.type]?.[answer];
    if (!src) return null;
    return (
      <Box
        sx={{
          width:           36,
          height:          36,
          borderRadius:    '50%',
          background:      '#fff',
          boxShadow:       '0 1px 4px rgba(0,0,0,0.12)',
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          flexShrink:      0,
        }}
      >
        <Box
          component="img"
          src={src}
          alt="respuesta"
          sx={{ width: 26, height: 26, display: 'block', objectFit: 'contain' }}
        />
      </Box>
    );
  }

  if (question.type === 'rating') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
        <StarIcon sx={{ fontSize: 18, color: '#f5a623' }} />
        <Typography variant="caption" fontWeight={700}>{answer}</Typography>
      </Box>
    );
  }

  if (question.type === 'slider') {
    return <Typography variant="caption" fontWeight={700}>{answer}</Typography>;
  }

  if (question.type === 'radio') {
    const opt = question.options?.find(
      (o, idx) => (o.id?.toString() ?? idx.toString()) === answer
    );
    return (
      <Typography
        variant="caption"
        fontWeight={700}
        noWrap
        sx={{ maxWidth: 110, display: 'block' }}
      >
        {opt?.text || ''}
      </Typography>
    );
  }

  if (question.type === 'text') {
    return (
      <Typography
        variant="caption"
        noWrap
        sx={{ maxWidth: 130, fontStyle: 'italic', display: 'block' }}
      >
        “{answer}”
      </Typography>
    );
  }

  return null;
});
AnswerBadge.displayName = 'AnswerBadge';

const SUMMARY_QUESTION_MAX_CHARS = 50;

const truncateSummaryText = (text = '') =>
  text.length > SUMMARY_QUESTION_MAX_CHARS
    ? `${text.slice(0, SUMMARY_QUESTION_MAX_CHARS).trimEnd()}…`
    : text;

// El alto (20vh) y el `display: flex` base los da la clase
// `.stepped-summary-bubble` en SurveyResponse.css; aquí solo se agregan
// los estilos visuales (fondo, padding, cursor) vía sx.
const StepSummaryBubble = React.memo(({ question, answer, questionNumber, onEdit }) => (
  <Box
    onClick={onEdit}
    className="stepped-prev-bubble stepped-summary-bubble"
    sx={{
      justifyContent: 'space-between',
      gap:            1,
      p:              1.25,
      borderRadius:   4,
      background:     'rgba(0,0,0,0.035)',
      cursor:         onEdit ? 'pointer' : 'default',
      opacity:        0.6,
      transition:     'opacity 0.2s ease',
      '&:hover':      { opacity: 0.85 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
      <Box
        className="stepped-summary-number"
        sx={{
          width:           28,
          height:          28,
          borderRadius:    '50%',
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          flexShrink:      0,
          background:      '#fff',
          border:          '2px solid var(--primary)',
          color:           'var(--primary)',
          fontWeight:      700,
          fontSize:        '0.85rem',
          boxShadow:       '0 2px 6px rgba(25, 118, 210, 0.25)',
        }}
      >
        {questionNumber}
      </Box>
      <Typography
        variant="body1"
        color="text.secondary"
        noWrap
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, fontSize: '1.2rem' }}
      >
        {truncateSummaryText(question.text)}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
      <AnswerBadge question={question} answer={answer} />
    </Box>
  </Box>
));
StepSummaryBubble.displayName = 'StepSummaryBubble';

// ─── ContinueArrow (reemplaza la burbuja "next") ─────────────────────────────
// Solo se muestra cuando la pregunta actual ya tiene respuesta, no es la
// última, no es de tipo texto, y no hay un auto-avance programado para
// ella (ver comentario más arriba, en el bloque del LAYOUT type 1). Le da
// al usuario una salida manual para avanzar cuando volvió a revisar una
// pregunta ya contestada.
const ContinueArrow = React.memo(({ onClick, label }) => (
  <Box
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    role="button"
    tabIndex={0}
    aria-label={label}
    className="stepped-continue-arrow"
  >
    <KeyboardArrowDownIcon sx={{ fontSize: 26 }} />
  </Box>
));
ContinueArrow.displayName = 'ContinueArrow';

export const SteppedFormLayout = React.memo(({
  sortedQuestions,
  answers,
  currentStepIndex,
  currentQuestionEntry,
  isLastQuestion,
  unansweredQuestions,
  onRatingChange,
  onSliderChange,
  onSliderCommit,
  onTextChange,
  onRadioChange,
  onEmojiChange,
  onGoToStep,
  autoAdvancePending,
  t,
}) => {
  const hasPrev = currentStepIndex > 0 && !!sortedQuestions[currentStepIndex - 1];

  const isTextStep = currentQuestionEntry?.[1]?.type === 'text';
  const isCurrentAnswered =
    !!currentQuestionEntry &&
    isQuestionAnswered(currentQuestionEntry[1], answers[currentQuestionEntry[0]]);
  // El botón fijo de abajo ya cubre "última pregunta" e "paso de texto";
  // la flecha solo aporta valor en los demás casos, y únicamente cuando
  // NO hay un auto-avance programado (si lo hay, la pregunta se acaba de
  // contestar y el propio auto-avance ya va a llevar al usuario a la
  // siguiente, sin necesidad de la flecha).
  const showContinueArrow =
    !isLastQuestion && !isTextStep && isCurrentAnswered && !autoAdvancePending;
  // El botón fijo de abajo se muestra en los mismos casos que en
  // SurveyResponse.jsx (showNextOrSubmitButton): última pregunta o texto.
  // Se usa acá solo para reservar espacio y que la pregunta no quede tapada.
  const reservesBottomSpace = isLastQuestion || isTextStep || showContinueArrow;

  return (
    <Box
      className={`stepped-layout-container${reservesBottomSpace ? ' stepped-layout-with-fixed-cta' : ''}`}
    >
      {hasPrev && (
        <StepSummaryBubble
          question={sortedQuestions[currentStepIndex - 1][1]}
          answer={answers[sortedQuestions[currentStepIndex - 1][0]]}
          questionNumber={currentStepIndex}
          onEdit={() => onGoToStep(currentStepIndex - 1)}
        />
      )}

      {currentQuestionEntry && (
        <Box className="stepped-main-question">
          <QuestionCard
            key={currentQuestionEntry[0]}
            qId={currentQuestionEntry[0]}
            question={currentQuestionEntry[1]}
            answer={answers[currentQuestionEntry[0]]}
            questionNumber={currentStepIndex + 1}
            onRatingChange={onRatingChange}
            onSliderChange={onSliderChange}
            onSliderCommit={onSliderCommit}
            onTextChange={onTextChange}
            onRadioChange={onRadioChange}
            onEmojiChange={onEmojiChange}
            showError={unansweredQuestions.has(currentQuestionEntry[0])}
            t={t}
          />
        </Box>
      )}

      {showContinueArrow && (
        <ContinueArrow
          onClick={() => onGoToStep(currentStepIndex + 1)}
          label={t.nextQuestion || 'Siguiente'}
        />
      )}
    </Box>
  );
});
SteppedFormLayout.displayName = 'SteppedFormLayout';

/* ============================================================================
   REGISTRO DE LAYOUTS
   ----------------------------------------------------------------------------
   Único punto que hay que tocar para agregar un nuevo diseño de encuesta.
   `isStepped: true` le dice a SurveyResponse.jsx que el flujo es de una
   pregunta a la vez (auto-avance, botón "Siguiente"/"Enviar", comentario
   solo al final). `isStepped: false` es el comportamiento "todo en una
   pantalla" (comentario y botón "Enviar" siempre visibles).

   Para agregar un nuevo type (ej. 3):
     1. Crear el componente (puede vivir en este mismo archivo o en uno
        nuevo, ej. `MiNuevoFormLayout.jsx`) recibiendo las mismas props
        que ClassicFormLayout/SteppedFormLayout/CardSwipeFormLayout.
     2. Registrarlo aquí: `3: { Component: MiNuevoFormLayout, isStepped: true|false }`
   No hace falta tocar SurveyResponse.jsx: la lógica de validación, envío
   y estado ya es genérica.
   ========================================================================== */
export const FORM_LAYOUTS = {
  0: { Component: ClassicFormLayout,    isStepped: false },
  1: { Component: SteppedFormLayout,    isStepped: true  },
  2: { Component: CardSwipeFormLayout,  isStepped: true  },
};
export const getFormLayout = (type) =>
  FORM_LAYOUTS[type] || FORM_LAYOUTS[DEFAULT_SURVEY_TYPE];